import { Dispatch } from 'redux';
import axios from '../../axios';
import * as actions from './actions.ts';
import { fireAnalytics } from '../../utils/firebase.js';
import { getSessionId, getDeviceCategory } from '../../utils/analyticsHelpers.ts';
import * as skillsService from '../../utils/onboardingV2/skillsService.ts';
import defaultPersonImage from '../../assets/images/userDefaultHelmet.png';
import defaultCompanyImage from '../../assets/images/companyDefaultLogo.png';
import { openMainModal } from '../actions/profileActions';
import { setLoginAnimation, setLoginAnimationType } from '../actions/authActions';

export const loadOnboardingSkillsAndCategories = () => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch({ type: actions.ONBOARDING_V2_GET_SKILLS_START });
      const [categories, skills] = await Promise.all([
        skillsService.fetchCategories(),
        skillsService.fetchSkillList(),
      ]);
      dispatch({
        type: actions.ONBOARDING_V2_GET_SKILLS_SUCCESS,
        payload: skills,
      });
      dispatch(actions.setSkillCategories(categories));
      return skills;
    } catch (err: any) {
      if (err?.response?.status === 401) {
        dispatch({
          type: actions.ONBOARDING_V2_GET_SKILLS_FAIL,
          payload: 'No autorizado',
        });
        dispatch(openMainModal({
          title: 'Sesión requerida',
          body: 'Para acceder al onboarding tenés que registrarte o iniciar sesión.',
          iconType: 'warning',
          isSuccess: false,
          yesLabel: 'Iniciar sesión',
          noLabel: 'Registrarme',
          confirmHandler: () => { window.location.href = '/'; },
          noHandler: () => { window.location.href = '/register'; },
        }));
        return;
      }
      const errorMessage = err?.response?.data?.message || 'Error al obtener las habilidades';
      dispatch({
        type: actions.ONBOARDING_V2_GET_SKILLS_FAIL,
        payload: errorMessage,
      });
      throw err;
    }
  };
};

export const getCitiesV2 = (provinceId: number) => {
  return (dispatch: Dispatch) => {
    const sendUrl = `/job-request/jurisdiccion/cities/list?province=${provinceId}`;
    
    dispatch({ type: actions.ONBOARDING_V2_GET_CITIES_START });
    
    return axios
      .get(sendUrl, {
        headers: {
          Authorization: 'Basic ZWRpZnk6bGFjbGF2ZXNlY3JldGExMjM0',
        },
      })
      .then((res) => {
        const cities = res.data?.cities ?? (Array.isArray(res.data) ? res.data : []);
        dispatch({
          type: actions.ONBOARDING_V2_GET_CITIES_SUCCESS,
          payload: cities,
        });
        return cities;
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || 'Error al obtener las ciudades';
        dispatch({
          type: actions.ONBOARDING_V2_GET_CITIES_FAIL,
          payload: errorMessage,
        });
        throw err;
      });
  };
};

function dataURLtoFile(dataurl: string, filename: string): File {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export const updateUserProfileV2 = () => {
  return async (dispatch: Dispatch, getState: any) => {
    try {
      dispatch({ type: actions.ONBOARDING_V2_UPDATE_PROFILE_START });
      dispatch(setLoginAnimationType('onboarding'));
      dispatch(setLoginAnimation(true));
      
      const state = getState();
      const onboardingState = state.onboardingV2;
      
      const {
        accountType,
        personaForm,
        empresaForm,
        userSkills,
        workZones,
        allCountry,
        userImage,
        isFromSkipOnboarding,
        preferredProfileCategories,
      } = onboardingState;
      
      const formData = new FormData();
      
      if (accountType === 'PERSON') {
        if (personaForm.name) formData.append('name', personaForm.name);
        if (personaForm.lastName) formData.append('last_name', personaForm.lastName);
        if (personaForm.tel) formData.append('phone', personaForm.tel);
        if (personaForm.webSite) formData.append('url_site_web', personaForm.webSite);
        if (personaForm.facebook) formData.append('facebook', personaForm.facebook);
        if (personaForm.linkedin) formData.append('linkedin', personaForm.linkedin);
        if (personaForm.twitter) formData.append('twitter', personaForm.twitter);
        if (personaForm.instagram) formData.append('instagram', personaForm.instagram);
      } else if (accountType === 'COMPANY') {
        if (empresaForm.companyName) formData.append('razon_social', empresaForm.companyName);
        if (empresaForm.tel) formData.append('phone', empresaForm.tel);
        if (empresaForm.webSite) formData.append('url_site_web', empresaForm.webSite);
        if (empresaForm.facebook) formData.append('facebook', empresaForm.facebook);
        if (empresaForm.linkedin) formData.append('linkedin', empresaForm.linkedin);
        if (empresaForm.twitter) formData.append('twitter', empresaForm.twitter);
        if (empresaForm.instagram) formData.append('instagram', empresaForm.instagram);
      }
      
      const descriptionValue = accountType === 'PERSON' ? personaForm.bio : empresaForm.bio;
      if (descriptionValue) formData.append('description', descriptionValue);
      
      if (accountType) formData.append('type', accountType);
      
      if (userSkills.length > 0) {
        const skillIds = userSkills.map(skill => skill.id).filter(Boolean);
        formData.append('skillId', skillIds.join(','));
      } else {
        throw new Error('Debe seleccionar al menos una habilidad');
      }
      
      if (preferredProfileCategories && preferredProfileCategories.length > 0) {
        const ids = preferredProfileCategories
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id));
        if (ids.length > 0) {
          formData.append('categoryConnect', ids.join(','));
        }
      }
      
      if (allCountry) {
        formData.append('allCountry', 'true');
      } else if (workZones.length > 0) {
        const jobBoardZones = workZones
          .map((zone: any) => {
            const jurisdictionId = zone.jurisdiccion?.id ?? zone.jurisdictionId;
            const cityName = zone.city || zone.name || '';
            if (!jurisdictionId || !cityName) {
              return null;
            }
            return { jurisdiccion: jurisdictionId, city: cityName };
          })
          .filter(Boolean);
        formData.append('jobBoardZones', JSON.stringify(jobBoardZones));
        formData.append('allCountry', 'false');
      } else {
        throw new Error('Debe seleccionar al menos una zona de trabajo o marcar "Todo el país"');
      }
      
      let imageFile: File;
      if (userImage) {
        imageFile = dataURLtoFile(userImage, 'userImage.jpg');
      } else {
        const defaultSrc = (accountType === 'COMPANY' ? defaultCompanyImage : defaultPersonImage) as string;
        const res = await fetch(defaultSrc);
        const blob = await res.blob();
        const mime = blob.type || 'image/png';
        const filename = accountType === 'COMPANY' ? 'defaultCompanyLogo.png' : 'defaultPersonImage.png';
        imageFile = new File([blob], filename, { type: mime });
      }
      formData.append('image', imageFile);

      const authToken = localStorage.getItem('access-Token-Edify');
      const config = { headers: { Authorization: `Bearer ${authToken}` } };
      
      const response = await axios.patch('/user/update', formData, config);
      
      const userDataResponse = await axios.get('/user/data', config);
      dispatch({ type: 'GET_DATA', payload: userDataResponse.data });
      
      dispatch({
        type: actions.ONBOARDING_V2_UPDATE_PROFILE_SUCCESS,
        payload: response.data,
      });
      
      fireAnalytics('onboarding_completed', {
        category: 'onboarding',
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        action: 'onboarding_completed',
        account_type: accountType,
        is_skip: isFromSkipOnboarding,
      });

      localStorage.removeItem('persist:onboardingV2');

      return userDataResponse.data;
      
    } catch (error: any) {
      const errorMessage = 'No pudimos guardar tu perfil. Intentá nuevamente en unos minutos.';
      dispatch({
        type: actions.ONBOARDING_V2_UPDATE_PROFILE_FAIL,
        payload: errorMessage,
      });
      dispatch(openMainModal({
        title: 'No pudimos guardar tu perfil',
        body: 'Ocurrió un problema al guardar tus datos. Intentá nuevamente en unos minutos.',
        iconType: 'error',
        isSuccess: true,
        confirmHandler: null,
      }));
      throw error;
    } finally {
      dispatch(setLoginAnimation(false));
      dispatch(setLoginAnimationType('login'));
    }
  };
};
